// How a refused box enrollment READS, for both transports (token mint and SSH
// pull) and every door that mounts them — the Fleet Connect a machine dialog,
// the onboarding box step, the donate wizard. `lib/fleet.ts` owns the POSTs; this
// owns the one thing that changes for a different reason — which refusal is which.
//
// **Branch on the machine half, never on the prose** (the `lib/keyProbeRefusal.ts`
// rule). Both REST enrollment doors refuse with the `enrollment-refused` 402
// (`enrollmentRefusedProblem`, `apps/api/src/routes/runners.ts`), whose `reason`
// member is the verdict of `enrollmentVerdict`, and its two kinds are not one
// thing:
//
//   - `seat_cap` — the fleet is full. Its `budget` member is the plan's limit,
//     and the refusal reads as the plan line's own limit sentence ("At 6, you
//     can't connect another…", `planCopy` in `lib/fleet-connect.ts`) — plain
//     copy, not an error state (#6567): nothing failed, the plan is the limit.
//   - `org_unfunded` — an ORG account with a box budget of 0. The free invite
//     seats reach a person's PERSONAL account only, so this is where someone
//     lands connecting a machine on their org. Its two fixes are not on the
//     dialog — paid seats for this org, or an operator's grant on the account —
//     so it renders as its own sentence naming both, with billing linked.
//
// A 402 with no `reason`, a `seat_cap` with no numeric `budget`, or a reason
// this client does not know, stays on the server's `detail`: the refusal is the
// API's, we only frame the kinds we know.

import { ApiError, errorReason } from './api';

/** A refused enrollment: the org-budget refusal by name, or the line to render. */
export type EnrollRefusal =
  | { readonly kind: 'org_unfunded' }
  /** The plan's machine limit is reached; `budget` is that limit. */
  | { readonly kind: 'seat_cap'; readonly budget: number }
  | {
      readonly kind: 'line';
      readonly message: string;
      /** A retryable refusal (a 503 transport blip) reads non-fatal. */
      readonly soft: boolean;
    };

/**
 * Frame a refused enrollment. `fallback` is the transport's own line for a body
 * that never came; `soft` marks the transport's retryable class. Only a 402 is a
 * budget refusal.
 */
export function enrollRefusal(error: unknown, fallback: string, soft = false): EnrollRefusal {
  if (error instanceof ApiError && error.status === 402) {
    const reason = error.extensions?.['reason'];
    if (reason === 'org_unfunded') return { kind: 'org_unfunded' };
    const budget = error.extensions?.['budget'];
    if (reason === 'seat_cap' && typeof budget === 'number' && Number.isSafeInteger(budget)) {
      return { kind: 'seat_cap', budget };
    }
  }
  return { kind: 'line', message: errorReason(error, fallback), soft };
}

/**
 * The refusal a TRANSPORT renders. With a dialog shell that owns a plan line
 * (`lifted`), a refusal about the plan (`seat_cap`, `org_unfunded`) is the
 * shell's to say once, so the transport keeps only the server's own line;
 * without one, the transport says everything.
 */
export function shownRefusal(refusal: EnrollRefusal | null, lifted: boolean): EnrollRefusal | null {
  if (refusal === null || !lifted) return refusal;
  return refusal.kind === 'line' ? refusal : null;
}
